import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Lock } from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await authAPI.login(formData.email, formData.password);
      login(res.data.access_token, res.data.user);
      toast.success("Bentornato!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Branding (Dark) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] relative overflow-hidden">
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25px 25px, white 2px, transparent 0)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
        
        {/* Gold accent line */}
        <div className="absolute left-0 top-1/4 bottom-1/4 w-1 bg-gradient-to-b from-transparent via-[#C9A227] to-transparent" />
        
        <div className="relative z-10 flex flex-col justify-center px-16 max-w-lg mx-auto">
          <div className="mb-8">
            <h1 className="font-serif text-5xl text-white tracking-tight mb-2">
              Equivalencia
            </h1>
            <p className="text-[#C9A227] text-sm tracking-[0.4em] uppercase font-medium">
              Equipe
            </p>
          </div>
          
          <div className="w-16 h-0.5 bg-[#C9A227] mb-8" />
        </div>
      </div>

      {/* Right side - Login Form (Light) */}
      <div className="flex-1 flex items-center justify-center bg-[#F5F5F5] p-8">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-10">
            <h1 className="font-serif text-4xl text-gray-900">Equivalencia</h1>
            <p className="text-[#C9A227] text-xs tracking-[0.3em] uppercase mt-1 font-semibold">Equipe</p>
          </div>

          {/* Login card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[#C9A227]/10 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-[#C9A227]" strokeWidth={1.5} />
                </div>
                <h2 className="font-serif text-2xl text-gray-900">Accedi</h2>
              </div>
              <p className="text-gray-500 text-sm">Inserisci le credenziali del team</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2 block">
                  Email
                </Label>
                <Input
                  type="email"
                  data-testid="login-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="admin@equivalencia.com"
                  required
                  className="h-12 bg-gray-50 border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:border-[#C9A227] focus:ring-[#C9A227]/20"
                />
              </div>

              <div>
                <Label className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2 block">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    data-testid="login-password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="h-12 bg-gray-50 border-gray-200 rounded-xl text-gray-900 pr-12 focus:border-[#C9A227] focus:ring-[#C9A227]/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" strokeWidth={1.5} />
                    ) : (
                      <Eye className="w-5 h-5" strokeWidth={1.5} />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full h-12 bg-[#C9A227] text-white hover:bg-[#D4AF37] rounded-xl text-sm font-semibold tracking-wide uppercase shadow-lg shadow-amber-200/30 transition-all"
              >
                {loading ? "Accesso in corso..." : "Accedi"}
              </Button>
            </form>
          </div>

          <p className="text-center text-gray-400 text-xs mt-6">
            Accesso riservato al team Equivalencia
          </p>
        </div>
      </div>

      {/* Custom badge to cover "Made with Emergent" */}
      <div 
        className="fixed bottom-4 right-4 bg-[#1A1A1A] text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium border border-[#C9A227]/30"
        style={{ minWidth: '190px', zIndex: 2147483647 }}
      >
        <div className="w-6 h-6 rounded-full bg-[#C9A227] flex items-center justify-center text-black font-bold text-xs">
          E
        </div>
        <span>Equivalencia Team</span>
      </div>
    </div>
  );
};

export default LoginPage;
